import { differenceInCalendarDays, differenceInCalendarMonths, differenceInCalendarYears, format } from 'date-fns';
import { format as formatTz } from 'date-fns-tz';

export function formatYMD(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export function formatTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${hours}:${minutes}:${seconds}`;
}

export function formatFullDate(date: Date): string {
  return `${formatYMD(date)} ${formatTime(date)}`;
}

export function formatMMMD(date: Date): string {
  const month = date.toLocaleString('en-us', { month: 'short' });
  const day = date.getDate().toString().padStart(2, '0');

  return `${month} ${day}`;
}

export function getPreviousDay(currentDate: string | undefined): Date | undefined {
  if (!currentDate) return undefined;

  const endAtDate = new Date(currentDate);
  const previous = new Date(endAtDate.getTime());
  previous.setDate(endAtDate.getDate() - 1);

  return previous;
}

export function formatStringAsDate(currentDate: string | undefined): Date | undefined {
  if (!currentDate) return undefined;

  return new Date(currentDate);
}

export function nextDayAsStringWithDefaultTimezone(currentDate: Date | undefined): string | undefined {
  if (!currentDate) return undefined;

  const nextDay = new Date(currentDate.getTime());
  nextDay.setDate(currentDate.getDate() + 1);

  return `${formatYMD(nextDay)}T00:00:00.000Z`;
}

export function formatDateWithDefaultTimezone(currentDate: Date | undefined): string | undefined {
  if (!currentDate) return undefined;

  return `${formatYMD(currentDate)}T00:00:00.000Z`;
}

export function getCurrentDateWithDefaultTimezone(): Date {
  return new Date(formatYMD(new Date()));
}

/**  Returns the date of the current week's Monday relative to the date provided,
 A week is from Monday to Sunday
 */
export const getMondayForCurrentWeek = (date?: Date): Date => {
  const selectedDate = date || getCurrentDateWithDefaultTimezone();
  const day = selectedDate.getDay();
  const diff = selectedDate.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(selectedDate.setDate(diff));
};

/**
 * Standard event date range format (Figma): "25 Dec, 09:00 - 10:00 (UTC+02:00)".
 * Use for webinar, seminar, and live event date/time display across the app.
 */
const EVENT_DATE_RANGE_FORMAT_OPTIONS = {
  startFormat: 'd MMM yyyy, HH:mm',
  endFormat: 'HH:mm',
  showTimezone: true,
} as const;

/**
 * Formats an event date range in the standard product format with timezone.
 * Example: "25 Dec, 09:00 - 10:00 (UTC+02:00)"
 */
export function formatEventDateRangeWithTimezone(startTime: Date | string, endTime: Date | string): string {
  return formatTimeDependingOnUserTimezone({
    startTime,
    endTime,
    formatOptions: EVENT_DATE_RANGE_FORMAT_OPTIONS,
  });
}

/**
 * ISO 8601-style format for admin/manager tables: "2024-12-25 · 09:00 - 10:00"
 * No timezone suffix; times in user's local timezone.
 */
const EVENT_DATE_RANGE_ISO_FORMAT_OPTIONS = {
  startFormat: 'yyyy-MM-dd · HH:mm',
  endFormat: 'HH:mm',
  showTimezone: false,
} as const;

export function formatEventDateRangeISO(startTime: Date | string, endTime: Date | string): string {
  return formatTimeDependingOnUserTimezone({
    startTime,
    endTime,
    formatOptions: EVENT_DATE_RANGE_ISO_FORMAT_OPTIONS,
  });
}

export const formatTimeDependingOnUserTimezone = ({
  startTime,
  endTime,
  formatOptions,
}: {
  startTime: Date | string;
  endTime: Date | string;
  formatOptions?: {
    startFormat?: string;
    endFormat?: string;
    showTimezone?: boolean;
  };
}): string => {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);

  const startFormat = formatOptions?.startFormat || 'eeee, dd MMMM, yyyy · HH:mm';
  const endFormat = formatOptions?.endFormat || 'HH:mm';

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const formattedStart = formatTz(startDate, startFormat, { timeZone: userTimeZone });
  const formattedEnd = formatTz(endDate, endFormat, { timeZone: userTimeZone });

  if (formatOptions?.showTimezone) {
    const startOffset = format(startDate, 'xxx');
    const endOffset = format(endDate, 'xxx');

    if (startOffset !== endOffset) {
      return `${formattedStart} (UTC${startOffset}) - ${formattedEnd} (UTC${endOffset})`;
    }

    return `${formattedStart} - ${formattedEnd} (UTC${startOffset})`;
  }

  return `${formattedStart} - ${formattedEnd}`;
};

export const formatSingleDateDependingOnTimezone = ({
  date,
  formatOptions,
  timezoneOffsetInMinutes,
}: {
  date: Date | string;
  formatOptions?: {
    format?: string;
    showTimezone?: boolean;
  };
  timezoneOffsetInMinutes?: number;
}): string => {
  const targetDate = new Date(date);

  const targetFormat = formatOptions?.format || 'eeee, dd MMMM, yyyy · HH:mm';

  const offsetInMinutes = timezoneOffsetInMinutes ?? new Date().getTimezoneOffset();

  // Convert timezone offset to timezone string for date-fns-tz
  // date-fns-tz expects timezone strings like 'America/New_York' or UTC offsets like 'UTC-05:00'
  // Since we only have offset in minutes, we'll construct a UTC offset string
  const offsetHours = Math.floor(Math.abs(offsetInMinutes) / 60);
  const offsetMinutes = Math.abs(offsetInMinutes) % 60;
  const offsetSign = offsetInMinutes >= 0 ? '-' : '+';
  const timezoneString = `UTC${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

  const formattedDate = formatTz(targetDate, targetFormat, { timeZone: timezoneString });

  const offsetHoursDisplay = Math.floor(offsetInMinutes / 60);
  const offsetMinutesDisplay = offsetInMinutes % 60;
  const offsetSignDisplay = offsetHoursDisplay > 0 ? '-' : '+';
  const offsetStr = `${offsetSignDisplay}${String(Math.abs(offsetHoursDisplay)).padStart(2, '0')}:${String(
    Math.abs(offsetMinutesDisplay),
  ).padStart(2, '0')}`;

  if (formatOptions?.showTimezone) {
    return `${formattedDate} (UTC${offsetStr})`;
  }

  return `${formattedDate}`;
};

export const formatSingleDateInUserTimezone = (date: Date | string, formatString = "MMMM d, yyyy 'at' HH:mm"): string => {
  const targetDate = new Date(date);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return formatTz(targetDate, formatString, { timeZone: userTimeZone });
};

const MS_MINUTE = 60 * 1000;
const MS_HOUR = 60 * MS_MINUTE;
const MS_DAY = 24 * MS_HOUR;

export type FormatFutureCountdownOptions = {
  /** Compact variants where specified (e.g. "2h", "1d"); otherwise falls back to standard phrasing. */
  limitedSpace?: boolean;
  /** Next calendar day: append local time, e.g. "tomorrow at 15:05". Ignored when {@link FormatFutureCountdownOptions.limitedSpace} is true. */
  includeTimeIfTomorrow?: boolean;
};

/**
 * Relative time until `to` from `from`, following the product “Future” countdown copy spec
 * (minutes → hours → calendar days/weeks/months/years). No trailing period.
 *
 * Examples: `"in a minute"`, `"in 3 minutes"`, `"in 1 hour"`, `"tomorrow"`, `"in 2 weeks"`, `"in 1 month"`.
 */
export function formatFutureCountdown(from: Date, to: Date, options?: FormatFutureCountdownOptions): string {
  const limited = Boolean(options?.limitedSpace);
  const diffMs = to.getTime() - from.getTime();

  if (!Number.isFinite(diffMs) || diffMs <= 0) {
    return limited ? '1m' : 'in a minute';
  }

  if (diffMs < MS_MINUTE) {
    return limited ? '1m' : 'in a minute';
  }

  if (diffMs < MS_HOUR) {
    const minutes = Math.ceil(diffMs / MS_MINUTE);
    if (minutes >= 60) {
      return limited ? '1h' : 'in 1 hour';
    }
    if (limited) return `${minutes}m`;
    return minutes === 1 ? 'in 1 minute' : `in ${minutes} minutes`;
  }

  if (diffMs < MS_DAY) {
    const hours = Math.ceil(diffMs / MS_HOUR);
    if (limited) return `${hours}h`;
    return hours === 1 ? 'in 1 hour' : `in ${hours} hours`;
  }

  const calendarDays = differenceInCalendarDays(to, from);

  if (calendarDays === 1) {
    if (limited) return '1d';
    if (options?.includeTimeIfTomorrow) {
      return `tomorrow at ${format(to, 'HH:mm')}`;
    }
    return 'tomorrow';
  }

  if (calendarDays >= 2 && calendarDays <= 6) {
    if (limited) return `${calendarDays}d`;
    return `in ${calendarDays} days`;
  }

  if (calendarDays === 7) {
    return limited ? '1w' : 'in 1 week';
  }

  if (calendarDays >= 8 && calendarDays <= 27) {
    const weeks = Math.ceil(calendarDays / 7);
    if (limited) return `${weeks}w`;
    return weeks === 1 ? 'in 1 week' : `in ${weeks} weeks`;
  }

  if (calendarDays === 28) {
    return limited ? '1mo' : 'in 1 month';
  }

  const monthsApart = differenceInCalendarMonths(to, from);
  const yearsApart = differenceInCalendarYears(to, from);

  if (yearsApart >= 2) {
    return limited ? `${yearsApart}y` : `in ${yearsApart} years`;
  }

  if (yearsApart === 1) {
    return limited ? '1y' : 'in 1 year';
  }

  if (monthsApart >= 12) {
    return limited ? '1y' : 'in 1 year';
  }

  if (monthsApart >= 2) {
    return limited ? `${monthsApart}mo` : `in ${monthsApart} months`;
  }

  if (monthsApart === 1) {
    return limited ? '1mo' : 'in 1 month';
  }

  if (calendarDays >= 29) {
    return limited ? '1mo' : 'in 1 month';
  }

  return limited ? '1w' : 'in 1 week';
}
